import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd'

import { DeleteIcon } from '@pleio/icons/delete'
import { MoveVerticalIcon } from '@pleio/icons/move-vertical'
import { PaintIcon } from '@pleio/icons/paint'
import { RowEnlargeIcon } from '@pleio/icons/row-enlarge'
import { RowShrinkIcon } from '@pleio/icons/row-shrink'

import Popover from 'js/components/Popover'
import ActionsBar from 'js/page/PageEditor/components/ActionsBar'
import ActionsBarButton from 'js/page/PageEditor/components/ActionsBarButton'
import BgColorSetting from 'js/page/PageEditor/components/BgColorSetting'
import PageEditorContext from 'js/page/PageEditor/PageEditorContext'

type PageEditorRowActionsProps = {
    rowIndex: number
    backgroundColor?: string
    showBackgroundColorSetting: boolean
    setShowBackgroundColorSetting: (show: boolean) => void
    numberOfColumns: number
    isFullWidth: boolean
    snapshot: DraggableStateSnapshot
    provided: DraggableProvided
    show?: boolean
    onFocus?: () => void
    onBlur?: () => void
    className?: string
}

const PageEditorRowActions = ({
    rowIndex,
    backgroundColor,
    showBackgroundColorSetting,
    setShowBackgroundColorSetting,
    numberOfColumns,
    isFullWidth,
    snapshot,
    provided,
    show,
    onFocus,
    onBlur,
    className,
}: PageEditorRowActionsProps) => {
    const { t } = useTranslation()

    const { setRows, widgetEditing } = useContext(PageEditorContext)

    const handleRemove = () => {
        setRows((prev: any) =>
            prev.filter((_: any, idx: number) => idx !== rowIndex),
        )
    }

    const handleResize = () => {
        setRows((prev: any) =>
            prev.map((row: any, idx: number) =>
                idx === rowIndex ? { ...row, isFullWidth: !isFullWidth } : row,
            ),
        )
    }

    const editBackgroundColor = (val: string) => {
        setRows((prev: any) =>
            prev.map((row: any, idx: number) =>
                idx === rowIndex ? { ...row, backgroundColor: val } : row,
            ),
        )
    }

    const resizeLabel = isFullWidth
        ? t('page.shrink-row')
        : t('page.enlarge-row')

    return (
        <ActionsBar
            show={show}
            onFocus={onFocus}
            onBlur={onBlur}
            className={className}
        >
            {!snapshot.isDragging && numberOfColumns === 1 && (
                <ActionsBarButton onClick={handleResize} tooltip={resizeLabel}>
                    {isFullWidth ? <RowShrinkIcon /> : <RowEnlargeIcon />}
                </ActionsBarButton>
            )}
            {!snapshot.isDragging && (
                <Popover
                    open={showBackgroundColorSetting}
                    setOpen={setShowBackgroundColorSetting}
                    content={
                        <BgColorSetting
                            name={`${rowIndex}-background-color`}
                            title={t('page.row-color')}
                            value={backgroundColor || ''}
                            onChange={editBackgroundColor}
                            width="200px"
                            paddingIsEnabled={true}
                        />
                    }
                    side="top"
                    sideOffset={1}
                >
                    <ActionsBarButton
                        id={`${rowIndex}-background-color`}
                        variant="secondary"
                        disabled={!!widgetEditing}
                        tooltip={t('page.row-color')}
                    >
                        <PaintIcon />
                    </ActionsBarButton>
                </Popover>
            )}
            <ActionsBarButton
                as="div"
                variant={widgetEditing ? 'tertiary' : 'secondary'}
                disabled={!!widgetEditing}
                {...provided.dragHandleProps}
                aria-label={t('page.press-spacebar-to-move-row')}
                tooltip={t('page.move-row')}
            >
                <MoveVerticalIcon />
            </ActionsBarButton>
            {!snapshot.isDragging && (
                <ActionsBarButton
                    onClick={handleRemove}
                    aria-label={t('page.delete-row')}
                    tooltip={t('page.delete-row')}
                    data-testid="button-delete-row"
                >
                    <DeleteIcon />
                </ActionsBarButton>
            )}
        </ActionsBar>
    )
}

export default PageEditorRowActions
